import { Fragment, PropsWithChildren, useMemo } from "react";

import BoxContainer from "@hl/base-components/lib/BoxContainer";
import {
  FarcasterIcon,
  Image as ImageIcon,
} from "@hl/base-components/lib/assets/icons.generated";
import {
  BACKGROUND_COLOR,
  TEXT_COLOR,
} from "@hl/base-components/lib/theme/colors";
import { WEIGHT_BOLD } from "@hl/base-components/lib/theme/typography";
import AllowlistIcon from "@hl/shared-features/lib/assets/icons.generated/AllowlistIcon";
import CopyButton from "@hl/shared-features/lib/features/widgets/CopyButton";
import { networkByNameLookup } from "@hl/shared-features/lib/utils/blockExplorer";
import { maskAddress } from "@hl/shared-features/lib/utils/content";
import { Center, Divider, Group, Image, Space, Text } from "@mantine/core";
import { zeroAddress } from "viem";

import {
  ConditionType,
  CurrencyStandardType,
  Condition,
  CollectionStandardType,
} from "apollo/graphql.generated";
import BlockchainIcon from "components/BlockchainIcon";
import ConditionBox from "~/features/gates/ConditionBox";

export type Props = {
  conditions: Condition[];
  requireAll: boolean;
  onRemoveCondition: (index: number) => void;
  onEditCondition: (index: number) => void;
};

const SelectedConditions = ({
  conditions,
  onRemoveCondition,
  onEditCondition,
}: Props) => {
  // Explicitly skip the marketing access card
  return (
    <BoxContainer py={0}>
      {conditions
        .filter((c) => c.data != null)
        .map((condition, index) => {
          if (condition.type === ConditionType.CURRENCY) {
            return (
              <Fragment key={`${ConditionType.CURRENCY}-${index}`}>
                <SelectedCurrencyConditionCard
                  condition={condition}
                  onRemoveCondition={() => onRemoveCondition(index)}
                  onEditCondition={() => onEditCondition(index)}
                />
                {index !== conditions.length - 1 && <Divider size="xs" />}
              </Fragment>
            );
          }
          if (condition.type === ConditionType.COLLECTION) {
            return (
              <Fragment key={`${ConditionType.COLLECTION}-${index}`}>
                <SelectedCollectionConditionCard
                  condition={condition}
                  onRemoveCondition={() => onRemoveCondition(index)}
                  onEditCondition={() => onEditCondition(index)}
                />
                {index !== conditions.length - 1 && <Divider size="xs" />}
              </Fragment>
            );
          }
          if (condition.type === ConditionType.NFT) {
            return (
              <Fragment key={`${ConditionType.NFT}-${index}`}>
                <SelectedNFTConditionCard
                  condition={condition}
                  onRemoveCondition={() => onRemoveCondition(index)}
                  onEditCondition={() => onEditCondition(index)}
                />
                {index !== conditions.length - 1 && <Divider size="xs" />}
              </Fragment>
            );
          }

          if (condition.type === ConditionType.ATTRIBUTE) {
            return (
              <Fragment key={`${ConditionType.ATTRIBUTE}-${index}`}>
                <SelectedNFTConditionCard
                  condition={condition}
                  onRemoveCondition={() => onRemoveCondition(index)}
                  onEditCondition={() => onEditCondition(index)}
                />
                {index !== conditions.length - 1 && <Divider size="xs" />}
              </Fragment>
            );
          }

          if (condition.type === ConditionType.ALLOWLIST) {
            return (
              <Fragment key={`${ConditionType.ALLOWLIST}-${index}`}>
                <SelectedAllowlistConditionCard
                  onRemoveCondition={() => onRemoveCondition(index)}
                  onEditCondition={() => onEditCondition(index)}
                  allowlistLength={
                    condition.data?.allowlistLength ??
                    condition.data?.allowlist?.length ??
                    0
                  }
                  allowlistString={
                    condition.data?.allowlistRaw ??
                    condition.data?.allowlist?.join(", ") ??
                    ""
                  }
                />
                {index !== conditions.length - 1 && <Divider size="xs" />}
              </Fragment>
            );
          }
          if (condition.type === ConditionType.FARCASTER) {
            return (
              <Fragment key={`${ConditionType.FARCASTER}-${index}`}>
                <SelectedFarcasterConditionCard
                  onRemoveCondition={() => onRemoveCondition(index)}
                  onEditCondition={() => onEditCondition(index)}
                  followFID={condition.data?.followFID ?? ""}
                />
                {index !== conditions.length - 1 && <Divider size="xs" />}
              </Fragment>
            );
          }

          return <></>;
        })}
    </BoxContainer>
  );
};

export default SelectedConditions;

type SelectedCurrencyConditionCardProps = {
  condition: Condition;
  onRemoveCondition: () => void;
  onEditCondition?: () => void;
};

const SelectedCurrencyConditionCard = ({
  condition,
  onRemoveCondition,
  onEditCondition,
}: SelectedCurrencyConditionCardProps) => {
  const data = condition.data!;
  const networkDetails = networkByNameLookup(data.network);
  const amountString =
    data.address === zeroAddress
      ? `${condition.amount} ${networkDetails.currencySymbol}`
      : `${condition.amount} ${data.name}`;
  const standardText = useMemo(() => {
    if (data.standard?.toLowerCase() === "native") {
      return networkByNameLookup(data.network).displayName;
    }
    return `ERC20 • ${maskAddress(data.address!, 6, 4)}`;
  }, [data]);

  return (
    <>
      <ConditionBox
        image={
          data.standard === CurrencyStandardType.ERC20 && !!data.imageUrl ? (
            <Image
              src={data.imageUrl}
              placeholder={
                <Text size="sm" fw={WEIGHT_BOLD}>
                  ${data.name?.substr(0, 3)}
                </Text>
              }
              withPlaceholder
              fit="contain"
            />
          ) : (
            // <Center>{getNativeCurrencyIcon(data.network || "", 24)}</Center>
            <Center>
              <BlockchainIcon network={data.network} />
            </Center>
          )
        }
        description={
          <>
            <Text size={"xs"} color={TEXT_COLOR.SECONDARY}>
              Has a minimum balance of:
              {/*<ConditionValuePill>{condition.amount}</ConditionValuePill>:*/}
            </Text>

            <Space h={6} />
            <Text fw={WEIGHT_BOLD} size={"sm"}>
              {amountString}
            </Text>
            <Text size={"xs"} color={TEXT_COLOR.SECONDARY}>
              {standardText}
            </Text>
          </>
        }
        onRemoveCondition={onRemoveCondition}
        onEditCondition={onEditCondition}
      />
    </>
  );
};

type SelectedCollectionConditionCardProps = {
  condition: Condition;
  onRemoveCondition: () => void;
  onEditCondition?: () => void;
};

const SelectedCollectionConditionCard = ({
  condition,
  onRemoveCondition,
  onEditCondition,
}: SelectedCollectionConditionCardProps) => {
  const data = condition.data!;
  const standard = data.standard ?? "";
  const networkDetails = networkByNameLookup(data.network);
  const details = useMemo(() => {
    const network = CollectionStandardType.OPENSEA
      ? "OpenSea Collection"
      : `${networkDetails.displayName} • ${standard} Collection`;

    const token =
      data.tokenId != null &&
      ` • Token ID ${
        data.tokenId.length > 8 ? maskAddress(data.tokenId, 4, 4) : data.tokenId
      }`;

    return (
      <>
        {network}
        {token}
      </>
    );
  }, [data, networkDetails, standard]);

  return (
    <>
      <ConditionBox
        rounded
        image={
          <Image
            src={data.imageUrl}
            fit="contain"
            withPlaceholder
            placeholder={<ImageIcon width={24} height={24} />}
          />
        }
        description={
          <>
            {data.tokenId ? (
              <Text size={"xs"} color={TEXT_COLOR.SECONDARY}>
                Holds this Token:
              </Text>
            ) : (
              <Text size={"xs"} color={TEXT_COLOR.SECONDARY}>
                Holds any{" "}
                <ConditionValuePill>{condition.amount}</ConditionValuePill>{" "}
                token(s) from:
              </Text>
            )}
            <Space h={6} />
            <Text fw={WEIGHT_BOLD} size={"sm"}>
              {data.name}
            </Text>
            <Text size={"xs"} color={TEXT_COLOR.SECONDARY}>
              {details}
            </Text>
          </>
        }
        onRemoveCondition={onRemoveCondition}
        onEditCondition={onEditCondition}
      />
    </>
  );
};

type SelectedNFTConditionCardProps = {
  condition: Condition;
  onRemoveCondition: () => void;
  onEditCondition?: () => void;
};

const SelectedNFTConditionCard = ({
  condition,
  onRemoveCondition,
  onEditCondition,
}: SelectedNFTConditionCardProps) => {
  const data = condition.data!;

  const collectionOrAddress =
    data.standard?.toLowerCase() === "opensea"
      ? data.address
      : maskAddress(data.address, 6, 4);

  const networkDetails = networkByNameLookup(data.network);
  const details = useMemo(() => {
    const network = `${networkDetails.displayName} • ${collectionOrAddress}`;

    const token =
      data.tokenId != null &&
      ` • Token ID ${
        data.tokenId.length > 8 ? maskAddress(data.tokenId, 4, 4) : data.tokenId
      }`;

    return (
      <>
        {network}
        {token}
      </>
    );
  }, [data, networkDetails]);

  return (
    <>
      <ConditionBox
        rounded
        image={
          <Image
            src={data.imageUrl}
            fit="contain"
            withPlaceholder
            placeholder={<ImageIcon width={24} height={24} />}
          />
        }
        description={
          <>
            {data.tokenId ? (
              <Text size={"xs"} color={TEXT_COLOR.SECONDARY}>
                Holds this Token:
              </Text>
            ) : (
              <Text size={"xs"} color={TEXT_COLOR.SECONDARY}>
                Holds any{" "}
                <ConditionValuePill>{condition.amount}</ConditionValuePill>{" "}
                token(s) from:
              </Text>
            )}
            <Space h={6} />
            <Group spacing={0}>
              <Text fw={WEIGHT_BOLD} size={"sm"}>
                {data.name}
              </Text>
              {data.attributes?.length && (
                <>
                  <Text size={"sm"} color={TEXT_COLOR.SECONDARY}>
                    &nbsp;with&nbsp;
                  </Text>
                  <Text fw={WEIGHT_BOLD} size={"sm"}>
                    {data.attributes[0].key}: {data.attributes[0].value}
                  </Text>
                </>
              )}
            </Group>

            <Text size={"xs"} color={TEXT_COLOR.SECONDARY}>
              {details}
            </Text>
          </>
        }
        onRemoveCondition={onRemoveCondition}
        onEditCondition={onEditCondition}
      />
    </>
  );
};

type SelectedAllowlistConditionCardProps = {
  onRemoveCondition: () => void;
  onEditCondition?: () => void;
  allowlistLength: number;
  allowlistString: string;
};

const SelectedAllowlistConditionCard = ({
  onRemoveCondition,
  onEditCondition,
  allowlistLength,
  allowlistString,
}: SelectedAllowlistConditionCardProps) => {
  return (
    <>
      <ConditionBox
        image={<AllowlistIcon width={24} height={24} />}
        description={
          <>
            <Text size={"xs"} color={TEXT_COLOR.SECONDARY}>
              Is on the specified allowlist:
            </Text>
            <Space h={6} />
            <Group spacing={0}>
              <Text fw={WEIGHT_BOLD} size={"sm"}>
                {allowlistLength} wallet addresses
              </Text>
            </Group>

            <Text size={"xs"} color={TEXT_COLOR.SECONDARY}>
              <Group spacing={4}>
                <span>Copy addresses</span>
                <CopyButton copyValue={allowlistString} />
              </Group>
            </Text>
          </>
        }
        onRemoveCondition={onRemoveCondition}
        onEditCondition={onEditCondition}
      />
    </>
  );
};

type SelectedFarcasterConditionCardProps = {
  onRemoveCondition: () => void;
  onEditCondition?: () => void;
  followFID: string;
};

const SelectedFarcasterConditionCard = ({
  onRemoveCondition,
  onEditCondition,
  followFID,
}: SelectedFarcasterConditionCardProps) => {
  return (
    <>
      <ConditionBox
        image={<FarcasterIcon width={24} height={24} />}
        description={
          <>
            <Text size={"xs"} color={TEXT_COLOR.SECONDARY}>
              Follow Farcaster account with FID:
            </Text>
            <Space h={6} />
            <Group spacing={0}>
              <Text fw={WEIGHT_BOLD} size={"sm"}>
                {followFID}
              </Text>
            </Group>
          </>
        }
        onRemoveCondition={onRemoveCondition}
        onEditCondition={onEditCondition}
      />
    </>
  );
};

// A placeholder for select component
const ConditionValuePill = (props: PropsWithChildren) => {
  return (
    <Text
      span
      py={2}
      px={6}
      fw={WEIGHT_BOLD}
      inherit
      bg={BACKGROUND_COLOR.SECONDARY}
      sx={{ borderRadius: 6 }}
    >
      {props.children}
    </Text>
  );
};
